"use client"

import type React from "react"
import { useEffect, useState, useRef, useCallback } from "react"
import { FiLoader, FiKey, FiCheck, FiUsers, FiSettings, FiTrendingUp, FiMapPin } from "react-icons/fi"
import toast from "react-hot-toast"
import api from "../../libs/axios"

interface Permission {
    id: number
    name: string
    description: string
    resource: string
    action: string
}

interface GroupedPermissions {
    [resource: string]: Permission[]
}

interface PaginationState {
    current_page: number
    per_page: number
    total: number
    total_pages: number
    has_next_page: boolean
    has_prev_page: false
    loading: boolean
    next_page: number | null
    prev_page: number | null
}

interface PermissionsProps {
    selectedPermissions: number[]
    onPermissionChange: (permissionId: number, checked: boolean) => void
    onModuleSelectAll: (module: string, checked: boolean) => void
    errors?: {
        permissions?: string
    }
    isVisible: boolean
    resetTrigger?: boolean
    // Optional props for standalone permission management
    roleId?: string
    standalone?: boolean
}

const Permissions: React.FC<PermissionsProps> = ({
    selectedPermissions,
    onPermissionChange,
    onModuleSelectAll,
    errors,
    isVisible,
    resetTrigger = false,
}) => {
    const [permissionsByModule, setPermissionsByModule] = useState<GroupedPermissions>({})
    const [displayedModules, setDisplayedModules] = useState<string[]>([])
    const [loading, setLoading] = useState(false)
    const [pagination, setPagination] = useState<PaginationState>({
        current_page: 1,
        per_page: 20,
        total: 0,
        total_pages: 0,
        has_next_page: true,
        has_prev_page: false,
        loading: false,
        next_page: null,
        prev_page: null,
    })

    const loadingRef = useRef<HTMLDivElement>(null)

    const getModuleIcon = (module: string) => {
        switch (module.toLowerCase()) {
            case "tournament":
                return <FiTrendingUp className="w-4 h-4" />
            case "match":
                return <FiCheck className="w-4 h-4" />
            case "team":
                return <FiUsers className="w-4 h-4" />
            case "ground":
                return <FiMapPin className="w-4 h-4" />
            case "user":
                return <FiUsers className="w-4 h-4" />
            default:
                return <FiSettings className="w-4 h-4" />
        }
    }

    const fetchPermissions = async (page = 1, resetData = true) => {
        try {
            if (page === 1) setLoading(true)
            else setPagination((prev) => ({ ...prev, loading: true }))

            const response = await api.get(`/permissions/grouped?page=${page}&limit=${pagination.per_page}`)

            const permissionsData = response.data.permissions
            const paginationData = response.data.pagination

            const formattedPermissions: GroupedPermissions = {}
            Object.keys(permissionsData).forEach((resource) => {
                const capitalizedResource = resource.charAt(0).toUpperCase() + resource.slice(1)
                formattedPermissions[capitalizedResource] = permissionsData[resource]
            })

            if (resetData || page === 1) {
                setPermissionsByModule(formattedPermissions)
                setDisplayedModules(Object.keys(formattedPermissions))
            } else {
                setPermissionsByModule((prev) => {
                    const merged = { ...prev }
                    Object.entries(formattedPermissions).forEach(([resource, permissions]) => {
                        if (merged[resource]) {
                            const existingIds = merged[resource].map((p) => p.id)
                            const newPermissions = permissions.filter((p) => !existingIds.includes(p.id))
                            merged[resource] = [...merged[resource], ...newPermissions]
                        } else {
                            merged[resource] = permissions
                        }
                    })
                    return merged
                })
                setDisplayedModules((prev) => {
                    const newModules = Object.keys(formattedPermissions)
                    return Array.from(new Set([...prev, ...newModules]))
                })
            }

            setPagination((prev) => ({
                ...prev,
                current_page: paginationData.current_page,
                per_page: paginationData.per_page,
                total: paginationData.total,
                total_pages: paginationData.total_pages,
                has_next_page: paginationData.has_next_page,
                has_prev_page: paginationData.has_prev_page,
                next_page: paginationData.next_page,
                prev_page: paginationData.prev_page,
                loading: false,
            }))
        } catch (error) {
            console.error("Error fetching permissions:", error)
            toast.error("Failed to load permissions")
            setPagination((prev) => ({ ...prev, loading: false }))
        } finally {
            if (page === 1) setLoading(false)
        }
    }

    const loadMorePermissions = useCallback(() => {
        if (!pagination.loading && pagination.has_next_page && pagination.next_page) {
            fetchPermissions(pagination.next_page, false)
        }
    }, [pagination.loading, pagination.has_next_page, pagination.next_page])

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && pagination.has_next_page && !pagination.loading) {
                    loadMorePermissions()
                }
            },
            { threshold: 0.1 },
        )

        if (loadingRef.current) observer.observe(loadingRef.current)
        return () => observer.disconnect()
    }, [loadMorePermissions, pagination.has_next_page, pagination.loading])

    useEffect(() => {
        if (isVisible) fetchPermissions(1, true)
    }, [isVisible])

    useEffect(() => {
        if (resetTrigger && isVisible) {
            setPermissionsByModule({})
            setDisplayedModules([])
            setPagination({
                current_page: 1,
                per_page: 10,
                total: 0,
                total_pages: 0,
                has_next_page: true,
                has_prev_page: false,
                loading: false,
                next_page: null,
                prev_page: null,
            })
            fetchPermissions(1, true)
        }
    }, [resetTrigger, isVisible])

    const handleModuleSelectAll = (module: string, checked: boolean) => {
        onModuleSelectAll(module, checked)
        const modulePermissions = permissionsByModule[module] || []
        modulePermissions.forEach((perm) => {
            const alreadySelected = selectedPermissions.includes(perm.id)
            if (checked && !alreadySelected) onPermissionChange(perm.id, true)
            if (!checked && alreadySelected) onPermissionChange(perm.id, false)
        })
    }

    const handlePermissionChange = (permissionId: number, checked: boolean) => {
        onPermissionChange(permissionId, checked)
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between border-b">
                <h3 className="text-lg font-medium text-gray-900 pb-2">
                    <FiKey className="inline mr-1" />
                    Permissions
                </h3>
                {pagination.total > 0 && (
                    <div className="text-sm text-gray-500">
                        Showing {displayedModules.length} modules • Page {pagination.current_page} of {pagination.total_pages}
                        {pagination.has_next_page && ` • Loading more...`}
                    </div>
                )}
            </div>
            <p className="text-sm text-gray-600">Select the permissions this role should have:</p>

            {errors?.permissions && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-red-600 text-sm">{errors.permissions}</p>
                </div>
            )}

            {loading ? (
                <div className="flex items-center justify-center py-8">
                    <FiLoader className="w-6 h-6 animate-spin text-primary-600" />
                    <span className="ml-2 text-gray-600">Loading permissions...</span>
                </div>
            ) : displayedModules.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No permissions available</div>
            ) : (
                <>
                    {displayedModules.map((module) => {
                        const modulePermissions = permissionsByModule[module] || []
                        const allSelected = modulePermissions.every((p) => selectedPermissions.includes(p.id))
                        const someSelected = modulePermissions.some((p) => selectedPermissions.includes(p.id))

                        return (
                            <div
                                key={module}
                                className={`border rounded-lg p-4 ${errors?.permissions ? "border-red-200 bg-red-50" : "border-gray-200"}`}
                            >
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center">
                                        {getModuleIcon(module)}
                                        <h4 className="text-md font-medium text-gray-900 ml-2">{module} Module</h4>
                                    </div>
                                    <label className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={allSelected}
                                            ref={(input) => {
                                                if (input) input.indeterminate = someSelected && !allSelected
                                            }}
                                            onChange={(e) => handleModuleSelectAll(module, e.target.checked)}
                                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                        />
                                        <span className="ml-2 text-sm text-gray-600">Select All</span>
                                    </label>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {modulePermissions.map((permission) => (
                                        <label key={permission.id} className="flex items-start">
                                            <input
                                                type="checkbox"
                                                checked={selectedPermissions.includes(permission.id)}
                                                onChange={(e) => handlePermissionChange(permission.id, e.target.checked)}
                                                className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                            />
                                            <div className="ml-2">
                                                <div className="text-sm font-medium text-gray-900 capitalize">{permission.name.replace(".", " ")}</div>
                                                <div className="text-xs text-gray-500">{permission.description}</div>
                                                <div className="text-xs text-gray-400 mt-1 capitalize">
                                                    {permission.action} • {permission.resource}
                                                </div>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )
                    })}

                    {pagination.has_next_page && (
                        <div ref={loadingRef} className="flex items-center justify-center py-6">
                            {pagination.loading ? (
                                <>
                                    <FiLoader className="w-5 h-5 animate-spin text-primary-600 mr-2" />
                                    <span className="text-gray-600">Loading more permissions...</span>
                                </>
                            ) : (
                                <div className="text-gray-400 text-sm">Scroll to load more permissions</div>
                            )}
                        </div>
                    )}

                    {!pagination.has_next_page && displayedModules.length > 0 && (
                        <div className="text-center py-4 text-gray-400 text-sm border-t">
                            All permissions loaded ({pagination.total} total items)
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

export default Permissions
